import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Tooltip } from "@sagot/ui";
import { useState, type ReactElement } from "react";
import type { ChannelOut } from "../shared/types";
import { ChannelCard, ChannelStatusBadge, ConversationsMetric } from "./channel-card";
import { useChannelSwitch, type ChannelPageContext } from "./channel-context";
import { CHANNEL_ICON, messengerDetail } from "./channels-model";
import { MessengerDisconnectDialog } from "./messenger-dialogs";

const P = "deployKnowledge.channels";

interface MessengerCardProps {
  channel: ChannelOut;
  ctx: ChannelPageContext;
  onConnect: () => void;
}

function Locked({ ctx, children }: { ctx: ChannelPageContext; children: ReactElement }) {
  if (ctx.canManage || !ctx.lockedReason) return children;
  return (
    <Tooltip content={ctx.lockedReason}>
      <span className="inline-flex">{children}</span>
    </Tooltip>
  );
}

/** Facebook Messenger: connect via the backend OAuth start URL, page + status, disconnect. */
export function MessengerCard({ channel, ctx, onConnect }: MessengerCardProps) {
  const { t } = useTranslation();
  const toggle = useChannelSwitch(ctx);
  const { connected, pageName } = messengerDetail(channel);
  const [disconnecting, setDisconnecting] = useState(false);
  const footer = connected ? (
    <>
      <ConversationsMetric count={channel.conversations_7d} />
      <Locked ctx={ctx}>
        <Button variant="ghost" size="sm" disabled={!ctx.canManage} onClick={() => { setDisconnecting(true); }}>
          {t(`${P}.messenger.disconnect`)}
        </Button>
      </Locked>
    </>
  ) : (
    <>
      <span className="text-xs text-fg-subtle">{t(`${P}.messenger.estimate`)}</span>
      <Locked ctx={ctx}>
        <Button size="sm" disabled={!ctx.canManage} onClick={onConnect}>
          <Icon name="plug" />
          {t(`${P}.messenger.connectPage`)}
        </Button>
      </Locked>
    </>
  );
  return (
    <>
      <ChannelCard
        testId="channel-messenger"
        icon={CHANNEL_ICON.messenger}
        muted={!connected}
        title={t(`${P}.names.messenger`)}
        toggle={toggle(channel, connected ? {} : { disabled: true, reason: undefined })}
        status={<ChannelStatusBadge channel={connected ? channel : { status: "disconnected" }} />}
        meta={connected ? pageName : undefined}
        description={t(connected ? `${P}.messenger.descriptionOn` : `${P}.messenger.descriptionOff`)}
        footer={footer}
      />
      {disconnecting ? <MessengerDisconnectDialog ctx={ctx} pageName={pageName} open onOpenChange={setDisconnecting} /> : null}
    </>
  );
}
